import { formatValidationMessage } from '@/common/application/utils/fieldNameFormatter';

export interface BackendValidationError {
  field: string;
  message: string;
}

/**
 * Parses a single validation error string from the API format:
 * "{ field = FieldName, message = Message text }"
 * Returns { field, message } or null if parsing fails.
 */
function parseOneValidationErrorString(item: string): BackendValidationError | null {
  const trimmed = item.trim();
  const fieldRegex = /\bfield\s*=\s*([^,]+)/i;
  const messageRegex = /\bmessage\s*=\s*(.+?)\s*}\s*$/s;
  const fieldMatch = fieldRegex.exec(trimmed);
  const messageMatch = messageRegex.exec(trimmed);
  if (fieldMatch && messageMatch && fieldMatch[1] !== undefined && messageMatch[1] !== undefined) {
    return {
      field: fieldMatch[1].trim(),
      message: messageMatch[1].trim()
    };
  }
  return null;
}

/**
 * Normalizes validation errors from the API into BackendValidationError[].
 * Handles: (1) array of strings like "{ field = X, message = Y }", (2) array of { field, message } objects.
 */
export function parseValidationErrorsFromApi(raw: unknown): BackendValidationError[] {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  const result: BackendValidationError[] = [];
  for (const item of raw) {
    if (typeof item === 'string') {
      const parsed = parseOneValidationErrorString(item);
      if (parsed) result.push(parsed);
    } else if (item && typeof item === 'object' && 'field' in item && 'message' in item) {
      const obj = item as { field: unknown; message: unknown };
      if (typeof obj.field === 'string' && typeof obj.message === 'string') {
        result.push({ field: obj.field, message: obj.message });
      }
    }
  }
  return result;
}

/**
 * Extracts and parses validation errors from a GraphQL/Apollo error.
 * Checks graphQLErrors[].extensions.validationErrors and networkError.result.errors[].extensions.validationErrors.
 */
export function getValidationErrorsFromGraphQLError(error: unknown): BackendValidationError[] {
  if (!error || typeof error !== 'object') return [];
  const err = error as {
    graphQLErrors?: Array<{ extensions?: { validationErrors?: unknown } }>;
    networkError?: { result?: { errors?: Array<{ extensions?: { validationErrors?: unknown } }> } };
  };
  const gqlValidationErrors = err.graphQLErrors?.[0]?.extensions?.validationErrors;
  if (gqlValidationErrors != null) {
    const parsed = parseValidationErrorsFromApi(gqlValidationErrors);
    if (parsed.length > 0) return parsed;
  }
  const networkValidationErrors = err.networkError?.result?.errors?.[0]?.extensions?.validationErrors;
  if (networkValidationErrors != null) {
    return parseValidationErrorsFromApi(networkValidationErrors);
  }
  return [];
}

/**
 * Maps backend validation field names to Formik field names
 */
const FIELD_NAME_MAP: Record<string, string> = {
  OnJobTime: 'onJobTime',
  LeaveTime: 'leaveTime',
  ShowUpTime: 'showUpTime',
  StartTime: 'startTime',
  JobDate: 'startDate',
  EstimatedHours: 'estimatedHours',
  EstimatedYards: 'estimatedYards',
  EstimatedArea: 'estimatedArea',
  CustomerId: 'customerId',
  JobSiteId: 'jobSiteId',
  TypeId: 'typeId',
  DispatcherId: 'dispatcherId',
  SalesAgentId: 'salesAgentId',
  DispatchLocationId: 'dispatchLocationId',
  PONumber: 'poNumber',
  COD: 'cod',
  VendorId: 'vendorId',
  Sequence: 'sequence',
  TimeZone: 'timeZone'
};

/**
 * Maps a backend validation field name to its corresponding Formik field name
 * @param backendFieldName - The field name from backend validation errors (e.g., "OnJobTime")
 * @returns The corresponding Formik field name (e.g., "onJobTime") or the original name if no mapping exists
 */
export const mapBackendFieldToFormikField = (backendFieldName: string): string => {
  return FIELD_NAME_MAP[backendFieldName] || backendFieldName;
};

/**
 * Maps backend validation errors to Formik field errors
 * @param validationErrors - Array of validation errors from backend
 * @returns Object with Formik field names as keys and error messages as values
 */
export const mapValidationErrorsToFormikErrors = (validationErrors: BackendValidationError[]): Record<string, string> => {
  const formikErrors: Record<string, string> = {};

  for (const error of validationErrors) {
    const formikFieldName = mapBackendFieldToFormikField(error.field);
    // Format the error message to be more user-friendly
    formikErrors[formikFieldName] = formatValidationMessage(error.message, error.field);
  }

  return formikErrors;
};
